import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect/DropdownSelect';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import { academicYears } from '../../../constants/academicYears';
import { useSchoolClassesConfigHook } from '../../common/hooks/useSchoolClassesConfig';
import dayjs, { Dayjs } from 'dayjs';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SnackbarSeverityType } from '../../../enums';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import { TransformedStudentDetail } from '../../../types/userType';
import pallet from '../../../layouts/pallet';

const MISC_FEE_CONFIG = {
  particulars: 'Miscellaneous Fee',
  feePeriod: 'One Time Payment',
  schemeName: 'One Time Payment',
  component: 'Miscellaneous Fee',
  paymentDueDate: '2027-05-31T00:00:00.000Z'
};

interface AddFeeDialogProps {
  open: boolean;
  onClose: () => void;
  school?: string;
  userId?: string;
  userDetails?: TransformedStudentDetail | null;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AddFeeDialog: React.FC<AddFeeDialogProps> = ({
  open,
  onClose,
  school,
  userId,
  userDetails,
  onShowToast
}) => {
  const { gradeOptions, divisionOptions } = useSchoolClassesConfigHook(school);

  const defaultAcademicYear = academicYears.find((year) => year.isDefault);
  const [academicYear, setAcademicYear] = useState<string>(
    defaultAcademicYear?.value || academicYears[0].value
  );
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [schemeName, setSchemeName] = useState('');
  const [particulars, setParticulars] = useState('');
  const [feePeriod, setFeePeriod] = useState('');
  const [component, setComponent] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [yearOfEstablishment, setYearOfEstablishment] = useState<Dayjs | null>(
    dayjs()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isMiscFee, setIsMiscFee] = useState(false);
  const [isMiscFeeDivisionLocked, setIsMiscFeeDivisionLocked] = useState(false);

  const isBPIS = school === 'BPIS';

  const applyMiscFeeConfig = useCallback(() => {
    const rollOverInfo = userDetails?.studentDetails?.rollOverInfo;
    const currentRollOver = rollOverInfo?.find(
      (info) => info.academicYear === academicYear
    );
    if (currentRollOver?.grade) {
      setGrade(currentRollOver.grade);
    }
    if (currentRollOver?.division) {
      setDivision(currentRollOver.division);
      setIsMiscFeeDivisionLocked(true);
    } else {
      setIsMiscFeeDivisionLocked(false);
    }
    setSchemeName(MISC_FEE_CONFIG.schemeName);
    setParticulars(MISC_FEE_CONFIG.particulars);
    setFeePeriod(MISC_FEE_CONFIG.feePeriod);
    setComponent(MISC_FEE_CONFIG.component);
    setYearOfEstablishment(dayjs(MISC_FEE_CONFIG.paymentDueDate));
  }, [userDetails, academicYear]);

  const resetState = useCallback(() => {
    setAcademicYear(defaultAcademicYear?.value || academicYears[0].value);
    setGrade('');
    setDivision('');
    setSchemeName('');
    setParticulars('');
    setFeePeriod('');
    setComponent('');
    setAmount(null);
    setYearOfEstablishment(dayjs());
    setIsLoading(false);
    setIsMiscFee(false);
    setIsMiscFeeDivisionLocked(false);
  }, [defaultAcademicYear]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleMiscFeeToggle = (checked: boolean) => {
    setIsMiscFee(checked);
    if (checked) {
      applyMiscFeeConfig();
    } else {
      setAcademicYear(defaultAcademicYear?.value || academicYears[0].value);
      setGrade('');
      setDivision('');
      setSchemeName('');
      setParticulars('');
      setFeePeriod('');
      setComponent('');
      setAmount(null);
      setYearOfEstablishment(dayjs());
      setIsMiscFeeDivisionLocked(false);
    }
  };

  const isSaveDisabled =
    !academicYear ||
    !grade ||
    !division ||
    !schemeName ||
    !particulars ||
    !feePeriod ||
    !component ||
    amount === null ||
    !yearOfEstablishment;

  const handleSave = async () => {
    setIsLoading(true);
    const formattedYearOfEstablishment = yearOfEstablishment
      ? yearOfEstablishment.format('YYYY-MM-DD')
      : '';

    if (!school) {
      onShowToast('School information is missing.', SnackbarSeverityType.error);
      console.error('School information is missing.');
      return;
    }

    if (!userId) {
      onShowToast('User ID is missing.', SnackbarSeverityType.error);
      console.error('User ID is missing.');
      return;
    }

    const payload = {
      session: academicYear,
      scheme: schemeName,
      particulars: particulars,
      feePeriod: feePeriod,
      component: component,
      amount: amount === null ? 0 : amount,
      paymentDueDate: formattedYearOfEstablishment,
      userId: userId,
      school: school,
      grade: grade,
      division: division
    };

    try {
      await paymentSchemesApis.createPaymentScheme(payload);
      onShowToast('Fee added successfully!', SnackbarSeverityType.success);
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Failed to create payment scheme:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to add fee.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const grades = gradeOptions;
  const divisions = divisionOptions;

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleSave}
      sx={{ textTransform: 'none' }}
      disabled={isSaveDisabled || isLoading}
    >
      Save
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add Fee'
      maxWidth='md'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      {isBPIS && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isMiscFee}
                onChange={(e) => handleMiscFeeToggle(e.target.checked)}
                sx={{ color: pallet.primary }}
              />
            }
            label={
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Record Miscellaneous Fee
              </Typography>
            }
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            value={academicYear}
            options={academicYears}
            onChange={(e) => setAcademicYear(e.target.value as string)}
            name='academicYear'
            fullWidth
            hasTopLabel
            disabled={isMiscFee}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Grade'
            placeholder='Select Grade'
            value={grade}
            options={grades}
            onChange={(e) => setGrade(e.target.value as string)}
            name='grade'
            fullWidth
            hasTopLabel
            required
            disabled={isMiscFee}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Division'
            placeholder='Select Division'
            value={division}
            options={divisions}
            onChange={(e) => setDivision(e.target.value as string)}
            name='division'
            fullWidth
            hasTopLabel
            required
            disabled={isMiscFee && isMiscFeeDivisionLocked}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Scheme Name'
            value={schemeName}
            onChange={(e) => setSchemeName(e.target.value)}
            fullWidth
            hasTopLabel
            required
            placeholder='e.g. One Time Fee'
            disabled={isMiscFee}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Particulars'
            value={particulars}
            onChange={(e) => setParticulars(e.target.value)}
            fullWidth
            hasTopLabel
            required
            placeholder='e.g. Laboratory Fee, Regular Boarding Fee'
            disabled={isMiscFee}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Fee Period'
            value={feePeriod}
            onChange={(e) => setFeePeriod(e.target.value)}
            fullWidth
            hasTopLabel
            required
            placeholder='e.g. One Time Fee'
            disabled={isMiscFee}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Component'
            value={component}
            onChange={(e) => setComponent(e.target.value)}
            fullWidth
            hasTopLabel
            required
            placeholder='e.g. Library, Transport'
            disabled={isMiscFee}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextInputWithIcon
            label='Amount'
            placeholder='Enter Amount'
            type='number'
            value={amount === null ? '' : amount}
            onChange={(e) =>
              setAmount(e.target.value === '' ? null : Number(e.target.value))
            }
            fullWidth
            hasTopLabel
            required
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Payment Due Date'
              onChange={(newValue: any) => {
                setYearOfEstablishment(newValue);
              }}
              value={yearOfEstablishment}
              width='100%'
              required
              disabled={isMiscFee}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 1 }} />
      </Box>
    </DialogModal>
  );
};

export default AddFeeDialog;
